// A2F-764: doc-type-driven sample bodies for the /pro-v2 live preview.
//
// One sample per v1 DocType. Each must surface every formatting knob
// the configurator exposes: H1 + H2 + body + bullet list + figure
// caption + table + table caption. So "change H1 color" repaints
// every sample's H1; "change body line-spacing" repaints every body
// para; etc.
//
// Content is short, realistic regulatory writing — not lorem ipsum.
// FDA/CMC content adapted from the Claude Design export's samples
// (regulatory accuracy already vetted there). General-scientific is
// re-mapped from the design's "lab" sample because v1 has no `lab`
// doctype; CMC and lab were redundant.

import type { DocType } from '../lib/telemetry-types'

export interface DocSample {
  /** Used as the faux Word title-bar filename. */
  filename: string
  h1: string
  h2a: string
  bodyA: string
  h2b: string
  bodyB: string
  bullets: string[]
  figureCaption: string
  tableCaption: string
  tableHeader: string[]
  tableRows: string[][]
}

export const PROV2_SAMPLES: Record<DocType, DocSample> = {
  fda: {
    filename: '3.2.P.5.4 Validation of Analytical Procedures.docx',
    h1: '3.2.P.5.4 Validation of Analytical Procedures',
    h2a: '3.2.P.5.4.1 Identity by RP-HPLC',
    bodyA:
      'The identity test for the drug substance is performed by reversed-phase HPLC with UV detection at 280 nm. Specificity was demonstrated by comparing the retention time of tessera-9 in samples to that of the reference standard. The retention time of tessera-9 was 7.2 ± 0.1 min across replicate injections.',
    h2b: '3.2.P.5.4.2 Assay and related substances',
    bodyB:
      'Linearity was demonstrated over the range of 25–125% of the nominal concentration (r ≥ 0.999). Accuracy was evaluated at three concentration levels with mean recovery of 99.4 ± 1.2%.',
    bullets: [
      'Mobile phase: 0.1% TFA in water (A) and 0.1% TFA in acetonitrile (B)',
      'Column: 4.6 × 150 mm, 3.5 µm C18',
      'Detection: UV at 280 nm',
      'Injection volume: 10 µL',
    ],
    figureCaption:
      'Figure 3.2.P.5.4-1. Representative chromatogram, tessera-9 reference standard at 100% nominal.',
    tableCaption:
      'Table 3.2.P.5.4-1. Stability summary, lot PLB-2201 (long-term, 25 °C / 60% RH).',
    tableHeader: ['Time (mo)', 'Assay (%)', 'Total Imp. (%)', 'Water (%)', 'Appearance'],
    tableRows: [
      ['0', '100.2', '0.21', '1.9', 'Conforms'],
      ['3', '99.7', '0.24', '2.0', 'Conforms'],
      ['6', '99.4', '0.27', '2.1', 'Conforms'],
      ['9', '99.1', '0.30', '2.1', 'Conforms'],
      ['12', '98.8', '0.33', '2.2', 'Conforms'],
    ],
  },
  protocols: {
    filename: '6.0 Study Endpoints.docx',
    h1: '6.0 Study Endpoints and Statistical Considerations',
    h2a: '6.1 Primary endpoint',
    bodyA:
      'The primary efficacy endpoint is the change from baseline in trough FEV₁ at Week 12. Analyses will follow ICH E9 principles, with treatment effect estimated by mixed model for repeated measures.',
    h2b: '6.2 Secondary endpoints',
    bodyB:
      "Time to first moderate or severe exacerbation, change from baseline in St. George's Respiratory Questionnaire total score, and rescue medication use over 12 weeks.",
    bullets: [
      'Informed consent obtained',
      'Confirmed COPD diagnosis (GOLD II–III)',
      'Stable on background therapy ≥ 4 weeks',
      'Able to perform spirometry per ATS/ERS',
    ],
    figureCaption:
      'Figure 6-1. Schedule of activities — visit windows ± 3 days.',
    tableCaption: 'Table 6-1. Eligibility criteria summary.',
    tableHeader: ['Criterion', 'Inclusion', 'Exclusion', 'Source'],
    tableRows: [
      ['Age', '40–80 yrs', '< 40, > 80', 'Visit 1'],
      ['Diagnosis', 'COPD, GOLD II–III', 'Asthma', 'Visit 1'],
      ['FEV₁/FVC', '< 0.70', '≥ 0.70', 'Visit 1'],
      ['Smoking hx', '≥ 10 pack-yrs', '< 10', 'Visit 1'],
      ['Hospitalized', '> 30 d ago', 'Within 30 d', 'Visit 2'],
    ],
  },
  cmc: {
    filename: '3.2.S.3.1 Elucidation of Structure.docx',
    h1: '3.2.S.3.1 Elucidation of Structure and Other Characteristics',
    h2a: '3.2.S.3.1.1 Structural elucidation',
    bodyA:
      'The chemical structure of tessera-9 was confirmed by ¹H NMR, ¹³C NMR, IR, UV, and mass spectrometry. Spectra were consistent with the proposed structure (C₁₈H₂₁N₃O₃, MW 343.4 g/mol).',
    h2b: '3.2.S.3.1.2 Physicochemical characterization',
    bodyB:
      'The drug substance is a white to off-white crystalline solid. Solubility was assessed across the physiological pH range; solubility decreases at pH > 6.5. The compound is non-hygroscopic at 25 °C / 60% RH.',
    bullets: [
      'Identity (RP-HPLC, IR)',
      'Assay (RP-HPLC)',
      'Related substances (RP-HPLC)',
      'Residual solvents (GC)',
      'Water content (Karl Fischer)',
    ],
    figureCaption:
      'Figure 3.2.S.3.1-1. Powder X-ray diffractogram, lot PLB-DS-204; characteristic 2θ peaks marked.',
    tableCaption: 'Table 3.2.S.3.1-1. Physicochemical properties of tessera-9.',
    tableHeader: ['Property', 'Method', 'Result', 'Specification'],
    tableRows: [
      ['Appearance', 'Visual', 'Off-white powder', 'White to off-white'],
      ['Melting point', 'DSC', '184.2 °C', '180–188 °C'],
      ['pKa', 'Potentiometric', '6.4 / 9.1', 'Report'],
      ['LogP', 'Shake-flask', '2.1', 'Report'],
      ['Particle size', 'Laser diff.', 'd₉₀ = 28 µm', 'd₉₀ NMT 50 µm'],
    ],
  },
  general: {
    filename: 'Method Validation Summary.docx',
    h1: '4.0 Method Validation Summary',
    h2a: '4.1 Specificity',
    bodyA:
      'Specificity was demonstrated by analysis of placebo, blank, and forced-degradation samples. No interference was observed at the retention time of the analyte.',
    h2b: '4.2 Linearity and range',
    bodyB:
      'Linearity was demonstrated across 50–150% of nominal concentration (r ≥ 0.999, n = 6 levels, three replicates each).',
    bullets: [
      'Specificity',
      'Linearity',
      'Range',
      'Accuracy',
      'Precision (repeatability + intermediate)',
      'Quantitation limit',
    ],
    figureCaption:
      'Figure 4-1. Linearity plot — peak area vs. nominal concentration.',
    tableCaption: 'Table 4-1. Validation parameter summary.',
    tableHeader: ['Parameter', 'Acceptance', 'Result', 'Pass/Fail'],
    tableRows: [
      ['Specificity', 'No interference', 'No interference', 'Pass'],
      ['Linearity', 'r ≥ 0.999', 'r = 0.9997', 'Pass'],
      ['Accuracy', '98–102% recovery', '99.4% (mean)', 'Pass'],
      ['Precision', 'RSD ≤ 2.0%', 'RSD = 0.7%', 'Pass'],
      ['Range', 'Bracket spec', '50–150%', 'Pass'],
    ],
  },
}
